import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router'
import { format } from 'date-fns'
import loadable from '@loadable/component'

import styled from 'styled-components'
import { getFiltersFromBounds, LocationToLatLngBounds } from 'utils/geo'
import { waitForGoogleMaps } from 'utils/googleMapsLoader'
import { serializeSearchState } from 'actions/utils/search'

// Imported from the dedicated module (not via the mapUtils barrel) so
// the home chunk doesn't pull the LatLng/LatLngBounds geo helpers and
// the rest of the map utilities along for the ride.
import { boundsToFloat } from 'components/maps/Map/boundsToFloat'

import { defaultStateCodeForMarket } from '../../utils'
import { CallToActionButton } from '../../shared'
import SelectLocation from './SelectLocation'
import ModalButton from './ModalButton'
import { Filters, Country, State, PrimaryCity } from '../types'

// FilterModal mounts a DateRangeCalendar (react-day-picker + date-fns)
// and a stack of QuantitySelect / WeekendOnly / AvailableWeekends
// inputs. Loading it eagerly pulled ~80 KB of calendar code onto every
// Navbar-bearing page; the modal only opens when the user clicks the
// "Filters" button so we defer the chunk until then.
const FilterModal = loadable(
  () => import(/* webpackChunkName: "home-filter-modal" */ './FilterModal'),
)

const SubmitButton = styled(CallToActionButton)`
  max-width: 100%;
  width: 100%;
  margin-top: 16px;
`

export const buildStateOptions = (
  states: Readonly<State[]>,
  primaryCity: PrimaryCity,
  marketCode: GQL_MarketCode,
) => {
  const nearPrimaryCityOption = {
    name: `Near ${primaryCity?.name}`,
    code: 'primary_city',
  }

  const matchingStateCode = defaultStateCodeForMarket(marketCode)

  const stateMatchesMarket = ({ code }: State) => code === matchingStateCode
  const stateOutsideOfMarket = ({ code }: State) => code !== matchingStateCode

  return [
    ...(states.filter(stateMatchesMarket) || []), // first option -> state that matches the current market
    ...(primaryCity != null ? [nearPrimaryCityOption] : []), // second option -> primary city of current market
    ...(states.filter(stateOutsideOfMarket) || []), // other options -> other states in the country
  ]
}

export const generateParams = (filters: Filters, primaryCity?: PrimaryCity) => {
  const {
    country,
    state,
    startDate,
    endDate,
    adults,
    children,
    pets,
    weekendMonths,
  } = filters

  const searchParams = new Map()

  searchParams.set('type', 'accommodation')
  searchParams.set('country', country)

  if (state === 'primary_city' && window.google?.maps?.Circle) {
    const { latitude, longitude } = primaryCity

    const lat = parseFloat(latitude)
    const lng = parseFloat(longitude)

    const bounds = LocationToLatLngBounds(
      {
        lat,
        lng,
      },
      200000, // 200 km
      google.maps.Circle,
    )

    const { sw, ne } = getFiltersFromBounds(bounds)

    searchParams.set('sw[]', boundsToFloat(sw))
    searchParams.set('ne[]', boundsToFloat(ne))
  } else if (state === 'primary_city') {
    // Maps unavailable (failed to load / blocked) — fall back to a market-wide search.
    searchParams.set('state', 'all')
  } else {
    searchParams.set('state', state || 'all')
  }

  if (startDate && endDate) {
    searchParams.set('startDate', format(startDate, 'yyyy-MM-dd'))
    searchParams.set('endDate', format(endDate, 'yyyy-MM-dd'))
    searchParams.delete('weekendMonths')
  }

  if (weekendMonths?.length > 0) {
    searchParams.set('weekendMonths', weekendMonths as unknown as string)
    searchParams.delete('startDate')
    searchParams.delete('endDate')
  }

  if (adults) {
    searchParams.set('adults', adults.toString())
  }

  if (children) {
    searchParams.set('children', children.toString())
  }

  if (pets) {
    searchParams.set('pets', pets.toString())
    searchParams.set('petFriendly', 'true')
  }

  return serializeSearchState(Object.fromEntries(searchParams))
}

interface HeaderSearchFormProps {
  marketCode?: GQL_MarketCode
  country?: Country
  countries?: Country[]
  states: State[]
  primaryCity?: PrimaryCity
}

const SearchForm = ({
  marketCode,
  country,
  countries,
  states,
  primaryCity,
}: HeaderSearchFormProps) => {
  const history = useHistory()
  const stateOptions = useMemo(() => {
    return marketCode ? buildStateOptions(states, primaryCity, marketCode) : []
  }, [states, primaryCity, marketCode])
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  // Track "has ever been opened" so the lazy chunk only fetches the
  // first time the user clicks the filter button, then stays mounted
  // (preserves partial filter selections across reopens).
  const [filterModalEverOpened, setFilterModalEverOpened] = useState(false)
  const openFilterModal = useCallback(() => {
    setFilterModalEverOpened(true)
    setFilterModalOpen(true)
  }, [])
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    weekendMonths: null,
    adults: 0,
    children: 0,
    pets: 0,
    country: country?.code || countries?.[0]?.code,
    state: marketCode ? stateOptions[0]?.code : null,
  })

  // The global home swaps to the visitor's detected market after hydration;
  // re-seed the location selection to that market's defaults while keeping
  // any dates/guests the user has already picked.
  const seededMarketCode = useRef(marketCode)
  useEffect(() => {
    if (!marketCode || marketCode === seededMarketCode.current) return

    seededMarketCode.current = marketCode
    setFilters((currentFilters) => ({
      ...currentFilters,
      country: country?.code ?? currentFilters.country,
      state: buildStateOptions(states, primaryCity, marketCode)[0]?.code,
    }))
  }, [marketCode, states, primaryCity, country?.code])

  const onDatesChange = useCallback(
    ({ startDate, endDate }: Pick<Filters, 'startDate' | 'endDate'>) => {
      setFilters((currentFilters) => ({
        ...currentFilters,
        startDate,
        endDate,
        weekendMonths: null,
      }))
    },
    [],
  )

  const onWeekendMonthsChange = useCallback((weekendMonths: string[]) => {
    setFilters((currentFilters) => ({
      ...currentFilters,
      weekendMonths,
      startDate: null,
      endDate: null,
    }))
  }, [])

  const onFilterChange = useCallback(
    (key: string, value: string | number) => {
      // Warm the on-demand Maps SDK as soon as the city option is picked so
      // it's ready by submit (generateParams needs google.maps.Circle).
      if (key === 'state' && value === 'primary_city') {
        waitForGoogleMaps().catch(() => {})
      }

      setFilters({
        ...filters,
        [key]: value,
      })
    },
    [filters],
  )

  const onSubmit = useCallback(async () => {
    if (filters.state === 'primary_city') {
      try {
        await waitForGoogleMaps()
      } catch (err) {
        // Adblock/network/CSP can block the SDK; generateParams then falls back
        // to a market-wide search, so log and proceed rather than dead-ending.
        // eslint-disable-next-line no-console
        console.warn(
          'Google Maps failed to load; searching without city bounds:',
          err,
        )
      }
    }

    const pathname = `/listings`
    const searchParams = generateParams(filters, primaryCity)

    history.push(
      {
        pathname,
        search: searchParams.toString(),
      },
      {
        primaryCityName: primaryCity?.name,
      },
    )

    window.scrollTo(0, 0)
  }, [filters, primaryCity])

  return (
    <>
      <SelectLocation
        onFilterChange={onFilterChange}
        // The state filter is re-seeded one effect after a detected market
        // arrives; fall back to the first option for that in-between render.
        value={
          marketCode
            ? (filters.state ?? stateOptions[0]?.code)
            : filters.country
        }
        type={marketCode ? 'state' : 'country'}
        options={marketCode ? stateOptions : (countries ?? [])}
      />
      <ModalButton filters={filters} onClick={openFilterModal} />
      {filterModalEverOpened && (
        <FilterModal
          filters={filters}
          open={filterModalOpen}
          onDatesChange={onDatesChange}
          onWeekendMonthsChange={onWeekendMonthsChange}
          onFilterChange={onFilterChange}
          onClose={() => setFilterModalOpen(false)}
          onSubmit={onSubmit}
        />
      )}
      <SubmitButton onClick={onSubmit} rounded>
        Search escapes
      </SubmitButton>
    </>
  )
}

export default SearchForm
